import { useEffect, useState, useMemo } from 'react';
import { useForm, FormProvider } from '@xmanscript/useform';
import { hasErrorBoundary } from '@xmanscript/has-error-boundary';
import { ScaleLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import defaultDp from '@/assets/image/default-dp.png';
import FormControl from '@/ui/_lib/FormControl';
// import { ReactComponent as TextIconLogo } from '@/assets/image/text-icon-logo.svg';
import { ReactComponent as TextLogo } from '@/assets/image/Logo-text.svg';
import Button from '@/ui/_lib/Button';
import ErrorLabel from '@/ui/_lib/FormControl/_lib/ErrorLabel';
import { ReactComponent as NextArrow } from '@/assets/image/RightArrow.svg';
import ImageUpload from '@/ui/_lib/ImageUpload';
import { useUser } from '@/contexts/UserContext/UserContext';
import { MediaService, UserService } from '@/ui/_apiUrls';

function CreateProfileFrom() {
  const [isMounted, setIsMounted] = useState(false);
  const { user, setUser } = useUser();
  const safeUser = user || {};
  const navigate = useNavigate();
  // Manually manage categories state - initialize with user's existing categories
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    // Initialize with user's existing categories if they exist
    if (safeUser.categories && Array.isArray(safeUser.categories)) {
      // Extract the id values from the category objects
      return safeUser.categories.map((cat) => cat.id || '').filter((id) => id);
    }
    return [];
  });
  // Track if categories have been touched
  const [isCategoriesTouched, setIsCategoriesTouched] = useState(false);

  // Use hardcoded categories instead of fetching from API
  const hardcodedCategories = useMemo(
    () => [
      { id: 'tech', name: 'Technology', label: 'Technology' },
      { id: 'business', name: 'Business', label: 'Business' },
      { id: 'education', name: 'Education', label: 'Education' },
      { id: 'health', name: 'Health', label: 'Health' },
      { id: 'entertainment', name: 'Entertainment', label: 'Entertainment' },
      { id: 'lifestyle', name: 'Lifestyle', label: 'Lifestyle' },
      { id: 'travel', name: 'Travel', label: 'Travel' },
      { id: 'food', name: 'Food', label: 'Food' },
    ],
    [],
  );

  const fetchedCategories = useMemo(() => ({ result: hardcodedCategories }), [hardcodedCategories]);
  const isCategoriesLoading = false;
  const isCategoriesError = false;

  const {
    mutate: patchUser,
    isError: isPatchUserError,
    isLoading: isPatchingUser,
  } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        // Update user context with the response
        const updatedUser = {
          ...safeUser,
          ...response.result,
          profileComplete: true,
        };
        setUser(updatedUser);

        // Navigate to choose template page after successful update
        setTimeout(() => {
          navigate('/choose-template');
        }, 100);
      },
      onError: () => {
        // Error updating user profile
      },
    },
  });

  const formMethods = useForm({
    formName: 'createProfileForm',
    initialValues: {
      name: safeUser.name || '',
      gender: safeUser.gender || '',
      // Don't include categories in initialValues since we're managing it manually
    },
    touchOnChange: true,
    validationSchema: (props: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { name, gender } = props;

      // Add proper null checks
      if (!name || typeof name !== 'string' || !name.trim()) {
        errors.name = 'Name is required.';
      }
      if (!gender) errors.gender = 'Gender is required.';
      // Category validation is handled manually through customCategoriesRegisterProps
      // to avoid duplicate error messages
      return errors;
    },
    submitHandler: async (vals) => {
      try {
        const {
          currentPacket: { image, ...rest },
        } = vals;
        // Include the manually managed categories
        const finalValues = { ...rest, categories: selectedCategories };

        if (image && image.length > 0) {
          const formData = new FormData();
          formData.append('file', image[0]);
          formData.append('title', `${rest.name.split(' ').join('-')}-thumbnail`);
          await postImage(formData);
        } else {
          await patchUser({ ...finalValues, profileComplete: true });
        }
      } catch (error: any) {
        // Form submission error handling
        throw new Error(error);
      }
    },
    persistValues: false, // Disable persistence to avoid FormProvider warnings
  });

  const { register, onSubmitHandler, values, formState, setValues } = formMethods;

  const {
    mutate: postImage,
    isSuccess: isPostingImageSuccess,
    data: postImageData,
    isLoading: isPostingImage,
  } = MediaService.post({
    isFormData: true,
    mutationParams: {
      onSuccess: async (response) => {
        // Get current values from form state
        const currentValues = formMethods.values;
        const { image, ...rest } = currentValues;
        await patchUser({ ...rest, thumbnail: response?.result?.url, profileComplete: true });
        setUser({ ...safeUser, thumbnail: response?.result?.url, profileComplete: true });
      },
      onError: () => {
        // Error uploading image
      },
    },
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isPostingImageSuccess) {
      setValues((prev) => {
        // @ts-ignore
        const { image, ...rest } = { ...prev };
        return { ...rest, thumbnail: postImageData?.result?.url };
      });
    }
  }, [isPostingImageSuccess, postImageData?.result?.url, setValues]);

  // Prepare categories for display - use fetched categories, fallback to defaults only if needed
  const categoryOptions = useMemo(() => {
    return fetchedCategories?.result?.length > 0
      ? fetchedCategories.result
      : [
          { _id: '1', name: 'Technology', label: 'Technology' },
          { _id: '2', name: 'Business', label: 'Business' },
          { _id: '3', name: 'Education', label: 'Education' },
          { _id: '4', name: 'Health', label: 'Health' },
          { _id: '5', name: 'Entertainment', label: 'Entertainment' },
        ];
  }, [fetchedCategories]);

  // Register the categories field
  const categoriesRegisterProps = register('categories');

  // Override the onChange to use our manual state
  const customCategoriesRegisterProps = {
    // Only pick specific properties from categoriesRegisterProps to avoid type conflicts
    id: categoriesRegisterProps.id,
    error: categoriesRegisterProps.error,
    haserror: categoriesRegisterProps.haserror,
    touched: categoriesRegisterProps.touched,
    enable: categoriesRegisterProps.enable,
    onTouchHandler: categoriesRegisterProps.onTouchHandler,
    // Override with our custom values
    value: selectedCategories,
    onChange: (newCategories: string[]) => {
      // Mark categories as touched when they change
      if (!isCategoriesTouched) {
        setIsCategoriesTouched(true);
      }
      setSelectedCategories(newCategories);
      // Also update the form values manually
      setValues((prev: any) => ({ ...prev, categories: newCategories }));
    },
    // Pass the touched error if categories have been touched and there's an error
    touchederror:
      isCategoriesTouched && (!selectedCategories || selectedCategories.length === 0)
        ? 'Categories must be selected.'
        : isCategoriesTouched && selectedCategories && selectedCategories.length > 1
          ? 'Select only one category.'
          : undefined,
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmitHandler(e);
  };

  if (!isMounted) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ScaleLoader />
      </div>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit} className="flex h-full w-full flex-col items-center justify-center gap-10">
        <TextLogo />
        <div className="form-controls flex w-full flex-col  items-center gap-4">
          <ImageUpload
            value={values.image ? URL.createObjectURL(values?.image[0]) : values.thumbnail || defaultDp}
            onChange={(data) => {
              setValues((prev) => ({
                ...prev,
                image: data,
              }));
            }}
          />
          <div className=" form-control-row flex w-full flex-col items-center gap-5 lg:flex-row">
            <FormControl
              controlType="input"
              label="Name *"
              className="w-full font-inter text-sm font-semibold text-[#475467]"
              {...register('name')}
            />
          </div>
          <div className="flex w-full justify-center">
            <FormControl
              requiredControl
              controlType="radio"
              options={[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
                { label: 'Other', value: 'other' },
              ]}
              choose="value"
              label="Gender"
              className="flex"
              {...register('gender')}
            />
          </div>
          <div className="w-full">
            <p className="mb-2 text-sm font-semibold text-[#475467]">Categories *</p>
            {isCategoriesLoading ? (
              <p className="w-full text-left">Loading categories...</p>
            ) : isCategoriesError ? (
              <p className="w-full text-left text-red-500">Error loading categories. Using default categories.</p>
            ) : (
              <FormControl
                controlType="chipSelector"
                maxCount={1} // Limit to 1 category selection
                options={categoryOptions}
                choose="_id" // Use _id as the identifier
                className="w-full"
                {...customCategoriesRegisterProps}
              />
            )}
          </div>

          {/* Removed bio field and profile toggle switches */}
        </div>
        <div className="form-actions flex flex-col gap-2">
          <div className="form-action flex flex-col gap-1">
            <Button
              className="mx-auto flex w-fit items-center justify-center gap-2"
              type="submit"
              variant={formState.hasError ? 'ghost' : 'primary'}
              style={formState.hasError ? { boxShadow: 'none' } : {}}
              disabled={formState.isSubmittingForm || isPatchingUser || isPostingImage}
            >
              <p>Complete Profile</p>
              {formState.isSubmittingForm || isPatchingUser || isPostingImage ? (
                <ScaleLoader height={20} color="#36d7b7" />
              ) : (
                <NextArrow />
              )}
            </Button>
            {(formState.isSubmitionError || isPatchUserError) && (
              <ErrorLabel
                message={
                  formState?.submitionError?.response?.data?.message || 'Something is not right. Please try again.'
                }
              />
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export default hasErrorBoundary(CreateProfileFrom);
